import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip, Dropdown, Input } from 'antd';
import {
  DeleteOutlined,
  FolderOpenOutlined,
  ReloadOutlined,
  SettingOutlined,
  QuestionCircleOutlined,
  ReadOutlined,
  TagOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  SearchOutlined,
  StarOutlined,
  StarFilled,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  CheckOutlined,
  CloseCircleFilled,
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faReply, faReplyAll, faShare,
  faEnvelope, faEnvelopeOpen,
  faFlag, faFlagCheckered,
  faArchive, faTrash, faTrashAlt,
  faPen, faFilter, faSortAmountDown,
  faColumns, faEye, faEyeSlash,
} from '@fortawesome/free-solid-svg-icons';

import {
  selectSelectedThreadId,
  selectSelectedFolderId,
  selectRibbonState,
  selectLayout,
  selectGraphStatus,
  setRibbonTab,
  setRibbonMode,
  setReadingPanePosition,
  setCompactMode,
  setInlineComposeSession,
  pushNotification,
  focusSearch,
  clearSelectedThread,
  selectSearch,
  setSearchQuery,
  clearSearch,
} from '../../../Slices/mailUiSlice';
import { searchMailThunk, moveThreadThunk, deleteThreadThunk } from '../../../Slices/Thunk/graphMailThunks';
import {
  selectThreadById,
  markThreadRead,
  markThreadUnread,
  setThreadFlagged,
} from '../../../Slices/mailThreadsSlice';
import {
  selectFolderByWellKnownName,
  selectFolderById,
} from '../../../Slices/mailFoldersSlice';
import {
  selectLatestMessageInThread,
  selectMessagesByThread,
} from '../../../Slices/mailMessagesSlice';

// ---------------------------------------------------------------------------
// Design tokens
// ---------------------------------------------------------------------------

const C = {
  blue:       '#0f6cbd',
  blueDark:   '#115ea3',
  blueLight:  '#eff6fc',
  blueBg:     '#deecf9',
  text:       '#242424',
  textSec:    '#605e5c',
  textMuted:  '#8a8886',
  border:     '#edebe9',
  bg:         '#fff',
  bgAlt:      '#faf9f8',
  bgHover:    '#f3f2f1',
  red:        '#c50f1f',
  redBg:      '#fde7e9',
  orange:     '#ca5010',
  green:      '#107c10',
  activeTab:  '#0f6cbd',
};
const FONT = "'Segoe UI', Tahoma, sans-serif";

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------

const TABS = [
  { id: 'home', label: 'Home' },
  { id: 'view', label: 'View' },
];

// ---------------------------------------------------------------------------
// Primitive sub-components
// ---------------------------------------------------------------------------

/** Single ribbon button — large (icon + label stacked) */
function RibbonButton({ icon, label, onClick, disabled = false, active = false,
  danger = false, title, children }) {
  return (
    <Tooltip title={title || label} mouseEnterDelay={0.8} placement="bottom">
      <button
        className={[
          'mr-btn',
          active   ? 'mr-btn--active'   : '',
          danger   ? 'mr-btn--danger'   : '',
          disabled ? 'mr-btn--disabled' : '',
        ].filter(Boolean).join(' ')}
        onClick={!disabled ? onClick : undefined}
        disabled={disabled}
        style={s.btn}
      >
        <span style={s.btnIcon}>{icon}</span>
        {label && <span style={s.btnLabel}>{label}</span>}
        {children}
      </button>
    </Tooltip>
  );
}

/** Small horizontal ribbon button (icon + inline label) */
function RibbonSmallButton({ icon, label, onClick, disabled = false,
  active = false, danger = false, title }) {
  return (
    <Tooltip title={title || label} mouseEnterDelay={0.8} placement="bottom">
      <button
        className={[
          'mr-sbtn',
          active   ? 'mr-sbtn--active'   : '',
          danger   ? 'mr-sbtn--danger'   : '',
          disabled ? 'mr-sbtn--disabled' : '',
        ].filter(Boolean).join(' ')}
        onClick={!disabled ? onClick : undefined}
        disabled={disabled}
        style={s.sbtn}
      >
        <span style={{ fontSize: 13 }}>{icon}</span>
        <span style={s.sbtnLabel}>{label}</span>
      </button>
    </Tooltip>
  );
}

/** Vertical separator between groups */
const Sep = () => <div style={s.sep} />;

/** Group of ribbon buttons with an optional label below */
function RibbonGroup({ label, children }) {
  return (
    <div style={s.group}>
      <div style={s.groupContent}>{children}</div>
      {label && <span style={s.groupLabel}>{label}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Home tab content
// ---------------------------------------------------------------------------

function HomeTab({ threadId, folderId, onOpenCompose }) {
  const dispatch = useDispatch();

  const thread        = useSelector(s => selectThreadById(s, threadId));
  const latestMsg     = useSelector(s => selectLatestMessageInThread(s, threadId));
  const threadMsgs    = useSelector(s => selectMessagesByThread(s, threadId));
  const messageIds    = threadMsgs.map(m => m.id);
  const currentFolder = useSelector(s => selectFolderById(s, folderId));
  const deletedFolder = useSelector(s => selectFolderByWellKnownName(s, 'deleteditems'));
  const archiveFolder = useSelector(s =>
    selectFolderByWellKnownName(s, 'archive') ||
    Object.values(s.mailFolders?.byId || {}).find(
      f => f.displayName?.toLowerCase() === 'archive'
    ) || null
  );
  const junkFolder    = useSelector(s => selectFolderByWellKnownName(s, 'junkemail'));

  const hasThread  = !!thread;
  const isUnread   = thread?.hasUnread ?? false;
  const isFlagged  = thread?.isFlagged ?? false;
  const isInTrash  = currentFolder?.wellKnownName === 'deleteditems';
  const isInJunk   = currentFolder?.wellKnownName === 'junkemail';

  // ── Actions ──────────────────────────────────────────────────────────────

  function openNewCompose() {
    const sid = `session_${Date.now()}`;
    dispatch(setInlineComposeSession(sid));
    dispatch(setRibbonMode('compose'));
    if (typeof onOpenCompose === 'function') onOpenCompose({ type: 'new' });
  }

  function handleReply() {
    if (!latestMsg || !onOpenCompose) return;
    onOpenCompose({ type: 'reply', message: latestMsg, conversationId: threadId });
  }

  function handleReplyAll() {
    if (!latestMsg || !onOpenCompose) return;
    onOpenCompose({ type: 'replyAll', message: latestMsg, conversationId: threadId });
  }

  function handleForward() {
    if (!latestMsg || !onOpenCompose) return;
    onOpenCompose({ type: 'forward', message: latestMsg, conversationId: threadId });
  }

  function handleDelete() {
    if (!thread) return;
    dispatch(clearSelectedThread());
    if (isInTrash) {
      dispatch(deleteThreadThunk({ conversationId: threadId, messageIds, folderId }));
    } else if (deletedFolder) {
      dispatch(moveThreadThunk({ conversationId: threadId, messageIds, fromFolderId: folderId, toFolderId: deletedFolder.id }));
    }
  }

  function handleArchive() {
    if (!thread || !archiveFolder) return;
    dispatch(clearSelectedThread());
    dispatch(moveThreadThunk({ conversationId: threadId, messageIds, fromFolderId: folderId, toFolderId: archiveFolder.id }));
  }

  function handleJunk() {
    if (!thread || !junkFolder) return;
    dispatch(clearSelectedThread());
    dispatch(moveThreadThunk({ conversationId: threadId, messageIds, fromFolderId: folderId, toFolderId: junkFolder.id }));
  }

  function handleMarkRead() {
    if (!thread) return;
    if (isUnread) {
      dispatch(markThreadRead({ conversationId: threadId }));
    } else {
      dispatch(markThreadUnread({ conversationId: threadId }));
    }
  }

  function handleFlag() {
    if (!thread) return;
    dispatch(setThreadFlagged({ conversationId: threadId, isFlagged: !isFlagged }));
  }

  // More → Move options
  const moveMenuItems = useMemo(() => [
    deletedFolder && {
      key: 'trash',
      label: 'Move to Deleted Items',
      icon: <DeleteOutlined />,
      onClick: handleDelete,
    },
    archiveFolder && {
      key: 'archive',
      label: 'Archive',
      icon: <FontAwesomeIcon icon={faArchive} />,
      onClick: handleArchive,
    },
    junkFolder && {
      key: 'junk',
      label: 'Mark as Junk',
      icon: <ExclamationCircleOutlined />,
      onClick: handleJunk,
    },
  ].filter(Boolean), [deletedFolder, archiveFolder, junkFolder, folderId, threadId]);

  return (
    <div style={s.tabContent}>

      {/* ── New ── */}
      <RibbonGroup label="New">
        <RibbonButton
          icon={<FontAwesomeIcon icon={faPen} style={{ fontSize: 18, color: C.blue }} />}
          label="New Email"
          title="New Email (Ctrl+N)"
          onClick={openNewCompose}
        />
      </RibbonGroup>
      <Sep />

      {/* ── Respond ── */}
      <RibbonGroup label="Respond">
        <RibbonButton
          icon={<FontAwesomeIcon icon={faReply} style={{ fontSize: 18, color: C.blue }} />}
          label="Reply"
          title="Reply (Ctrl+R)"
          disabled={!hasThread}
          onClick={handleReply}
        />
        <RibbonButton
          icon={<FontAwesomeIcon icon={faReplyAll} style={{ fontSize: 18, color: C.blue }} />}
          label="Reply All"
          title="Reply All (Ctrl+Shift+R)"
          disabled={!hasThread}
          onClick={handleReplyAll}
        />
        <RibbonButton
          icon={<FontAwesomeIcon icon={faShare} style={{ fontSize: 18, color: C.blue }} />}
          label="Forward"
          title="Forward (Ctrl+F)"
          disabled={!hasThread}
          onClick={handleForward}
        />
      </RibbonGroup>
      <Sep />

      {/* ── Delete ── */}
      <RibbonGroup label="Delete">
        <RibbonButton
          icon={<DeleteOutlined style={{ fontSize: 20, color: C.red }} />}
          label={isInTrash ? 'Delete' : 'Delete'}
          title={isInTrash ? 'Permanently Delete' : 'Move to Deleted Items'}
          disabled={!hasThread}
          danger
          onClick={handleDelete}
        />
        <RibbonButton
          icon={<FontAwesomeIcon icon={faArchive} style={{ fontSize: 18, color: C.textSec }} />}
          label="Archive"
          title="Archive message"
          disabled={!hasThread || !archiveFolder}
          onClick={handleArchive}
        />
        <Dropdown menu={{ items: moveMenuItems }} trigger={['click']}
          disabled={!hasThread} placement="bottomLeft">
          <div>
            <RibbonButton
              icon={<FolderOpenOutlined style={{ fontSize: 19, color: C.textSec }} />}
              label="Move"
              title="Move to folder"
              disabled={!hasThread}
            />
          </div>
        </Dropdown>
      </RibbonGroup>
      <Sep />

      {/* ── Tags ── */}
      <RibbonGroup label="Tags">
        <RibbonButton
          icon={
            isFlagged
              ? <StarFilled  style={{ fontSize: 19, color: C.orange }} />
              : <StarOutlined style={{ fontSize: 19, color: C.textSec }} />
          }
          label={isFlagged ? 'Unflag' : 'Flag'}
          title={isFlagged ? 'Remove flag' : 'Flag for follow-up'}
          active={isFlagged}
          disabled={!hasThread}
          onClick={handleFlag}
        />
        <RibbonButton
          icon={
            isUnread
              ? <FontAwesomeIcon icon={faEnvelopeOpen} style={{ fontSize: 18, color: C.blue }} />
              : <FontAwesomeIcon icon={faEnvelope}     style={{ fontSize: 18, color: C.textSec }} />
          }
          label={isUnread ? 'Mark Read' : 'Mark Unread'}
          title={isUnread ? 'Mark as read' : 'Mark as unread'}
          disabled={!hasThread}
          onClick={handleMarkRead}
        />
      </RibbonGroup>
      <Sep />

      {/* ── Find ── */}
      <RibbonGroup label="Find">
        <RibbonButton
          icon={<SearchOutlined style={{ fontSize: 19, color: C.blue }} />}
          label="Search"
          title="Focus search bar"
          onClick={() => dispatch(focusSearch())}
        />
      </RibbonGroup>
    </div>
  );
}

// ---------------------------------------------------------------------------
// View tab content
// ---------------------------------------------------------------------------

function ViewTab() {
  const dispatch = useDispatch();
  const layout   = useSelector(selectLayout);

  const readingPaneMenuItems = [
    {
      key: 'right',
      label: 'Right',
      icon: layout.readingPanePosition === 'right' ? <CheckOutlined /> : null,
      onClick: () => dispatch(setReadingPanePosition('right')),
    },
    {
      key: 'bottom',
      label: 'Bottom',
      icon: layout.readingPanePosition === 'bottom' ? <CheckOutlined /> : null,
      onClick: () => dispatch(setReadingPanePosition('bottom')),
    },
    {
      key: 'off',
      label: 'Off',
      icon: layout.readingPanePosition === 'off' ? <CheckOutlined /> : null,
      onClick: () => dispatch(setReadingPanePosition('off')),
    },
  ];

  return (
    <div style={s.tabContent}>

      {/* ── Layout ── */}
      <RibbonGroup label="Layout">
        <Dropdown menu={{ items: readingPaneMenuItems }} trigger={['click']}
          placement="bottomLeft">
          <div>
            <RibbonButton
              icon={<AppstoreOutlined style={{ fontSize: 19, color: C.blue }} />}
              label="Reading Pane"
              title="Change reading pane position"
            />
          </div>
        </Dropdown>

        <RibbonButton
          icon={<UnorderedListOutlined style={{ fontSize: 19, color: C.textSec }} />}
          label={layout.isCompact ? 'Normal' : 'Compact'}
          title={layout.isCompact ? 'Normal view' : 'Compact view'}
          active={layout.isCompact}
          onClick={() => dispatch(setCompactMode(!layout.isCompact))}
        />
      </RibbonGroup>
      <Sep />

      {/* ── Window ── */}
      <RibbonGroup label="Window">
        <RibbonButton
          icon={<ReloadOutlined style={{ fontSize: 19, color: C.textSec }} />}
          label="Refresh"
          title="Refresh mail"
          onClick={() => window.location.reload()}
        />
      </RibbonGroup>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Connectivity indicator
// ---------------------------------------------------------------------------

function ConnectivityDot({ status }) {
  const map = {
    connected:    { color: C.green,  label: 'Connected'    },
    degraded:     { color: C.orange, label: 'Slow connection' },
    disconnected: { color: C.red,    label: 'Disconnected' },
  };
  const info = map[status] || map.connected;
  return (
    <Tooltip title={info.label} mouseEnterDelay={0.4}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'default' }}>
        <div style={{
          width: 7, height: 7, borderRadius: '50%',
          background: info.color,
          boxShadow: `0 0 0 2px ${info.color}33`,
        }} />
        <span style={{ fontSize: 11, color: C.textMuted, fontFamily: FONT,
          display: 'none' /* show on wider ribbons */ }}>
          {info.label}
        </span>
      </div>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// RibbonSearch — inline search input for the tab bar
// ---------------------------------------------------------------------------

function RibbonSearch() {
  const dispatch    = useDispatch();
  const search      = useSelector(selectSearch);
  const debounceRef = useRef(null);
  const pendingRef  = useRef(null);
  const inputRef    = useRef(null);
  const [focused, setFocused] = useState(false);

  // When the Find › Search button calls focusSearch(), auto-focus the input
  useEffect(() => {
    if (search.phase === 'focused') {
      inputRef.current?.focus();
    }
  }, [search.phase]);

  const runSearch = useCallback((query) => {
    pendingRef.current?.abort();
    pendingRef.current = dispatch(searchMailThunk({ query }));
  }, [dispatch]);

  const handleChange = useCallback((e) => {
    const q = e.target.value;
    dispatch(setSearchQuery(q));
    clearTimeout(debounceRef.current);
    if (!q) {
      pendingRef.current?.abort();
      dispatch(clearSearch());
      return;
    }
    if (q.length < 2) return;
    debounceRef.current = setTimeout(() => runSearch(q), 400);
  }, [dispatch, runSearch]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && search.query.length >= 2) {
      clearTimeout(debounceRef.current);
      runSearch(search.query);
    }
    if (e.key === 'Escape') {
      pendingRef.current?.abort();
      dispatch(clearSearch());
      inputRef.current?.blur();
    }
  }, [dispatch, search.query, runSearch]);

  const handleClear = useCallback(() => {
    pendingRef.current?.abort();
    dispatch(clearSearch());
    inputRef.current?.focus();
  }, [dispatch]);

  const isSearching = search.phase === 'searching';
  const isActive    = focused || !!search.query;

  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      width: isActive ? 280 : 200,
      transition: 'width 0.2s ease',
    }}>
      <Input
        ref={inputRef}
        className="ribbon-search"
        prefix={
          <SearchOutlined style={{
            color: isSearching ? C.blue : C.textMuted,
            fontSize: 13,
            transition: 'color .15s',
          }} />
        }
        suffix={search.query
          ? <CloseCircleFilled
              style={{ color: C.textMuted, cursor: 'pointer', fontSize: 12 }}
              onClick={handleClear}
            />
          : null
        }
        placeholder="Search mail"
        value={search.query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { setFocused(true); dispatch(focusSearch()); }}
        onBlur={() => setFocused(false)}
        size="small"
        style={{
          background: isActive ? '#fff' : '#f3f2f1',
          border: `1px solid ${isActive ? C.blue : C.border}`,
          borderRadius: 4,
          fontSize: 12,
          height: 24,
          width: '100%',
          transition: 'background .15s, border-color .15s',
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// MailRibbon — root component
// ---------------------------------------------------------------------------
//
// Props:
//   onOpenCompose  ({ type, message, conversationId }) => void
//   style          React.CSSProperties — outer container overrides
// ---------------------------------------------------------------------------

export default function MailRibbon({ onOpenCompose, style }) {
  const dispatch    = useDispatch();
  const ribbon      = useSelector(selectRibbonState);
  const threadId    = useSelector(selectSelectedThreadId);
  const folderId    = useSelector(selectSelectedFolderId);
  const graphStatus = useSelector(selectGraphStatus);

  const activeTab = ribbon.activeTabId || 'home';

  const switchTab = useCallback((tabId) => {
    dispatch(setRibbonTab(tabId));
  }, [dispatch]);

  return (
    <>
      <style>{CSS}</style>

      <div style={{ ...s.root, ...style }}>

        {/* ── Tab bar ── */}
        <div style={s.tabBar}>
          <div style={s.tabList}>
            {TABS.map(tab => (
              <button
                key={tab.id}
                className={`mr-tab ${activeTab === tab.id ? 'mr-tab--active' : ''}`}
                onClick={() => switchTab(tab.id)}
                style={s.tab}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Right side: search + connectivity */}
          <div style={{
            marginLeft: 'auto', display: 'flex', alignItems: 'center',
            gap: 10, paddingRight: 12,
          }}>
            <RibbonSearch />
            <ConnectivityDot status={graphStatus} />
          </div>
        </div>

        {/* ── Tab panel ── */}
        <div style={s.panel}>
          {activeTab === 'home' && (
            <HomeTab
              threadId={threadId}
              folderId={folderId}
              onOpenCompose={onOpenCompose}
            />
          )}
          {activeTab === 'view' && <ViewTab />}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const s = {
  root: {
    display: 'flex', flexDirection: 'column',
    background: C.bg,
    borderBottom: `1px solid ${C.border}`,
    fontFamily: FONT,
    userSelect: 'none',
    flexShrink: 0,
  },

  // Tab bar
  tabBar: {
    display: 'flex', alignItems: 'center',
    borderBottom: `1px solid ${C.border}`,
    background: C.bgAlt,
    paddingLeft: 4,
    height: 32,
  },
  tabList: { display: 'flex', alignItems: 'flex-end', height: '100%' },
  tab: {
    height: '100%',
    padding: '0 14px',
    border: 'none',
    borderBottom: '2px solid transparent',
    background: 'transparent',
    color: C.textSec,
    fontSize: 12,
    fontWeight: 400,
    cursor: 'pointer',
    fontFamily: FONT,
    transition: 'color .12s, border-color .12s, background .12s',
    whiteSpace: 'nowrap',
  },

  // Panel
  panel: {
    overflowX: 'auto',
    overflowY: 'hidden',
    minHeight: 74,
  },
  tabContent: {
    display: 'flex', alignItems: 'stretch',
    padding: '4px 8px',
    gap: 0,
    minHeight: 66,
  },

  // Group
  group: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'stretch',
    padding: '0 6px',
    minWidth: 0,
  },
  groupContent: {
    display: 'flex', alignItems: 'flex-start',
    gap: 2, flex: 1,
  },
  groupLabel: {
    textAlign: 'center', fontSize: 10, color: C.textMuted,
    fontFamily: FONT, paddingTop: 3,
    borderTop: `1px solid ${C.border}`,
    marginTop: 4,
    display: 'block',
  },

  // Separator
  sep: {
    width: 1, background: C.border,
    margin: '4px 4px', alignSelf: 'stretch',
    flexShrink: 0,
  },

  // Large button
  btn: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'flex-start',
    gap: 3,
    padding: '5px 7px',
    border: '1px solid transparent',
    borderRadius: 4,
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: FONT,
    minWidth: 44, maxWidth: 64,
    transition: 'background .1s, border-color .1s',
  },
  btnIcon:  { fontSize: 20, lineHeight: 1, display: 'flex', alignItems: 'center' },
  btnLabel: {
    fontSize: 11, color: C.textSec, textAlign: 'center',
    lineHeight: 1.2, whiteSpace: 'nowrap',
  },

  // Small inline button
  sbtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '4px 8px',
    border: '1px solid transparent',
    borderRadius: 3,
    background: 'transparent',
    cursor: 'pointer', fontFamily: FONT,
    whiteSpace: 'nowrap',
    transition: 'background .1s',
  },
  sbtnLabel: { fontSize: 12, color: C.textSec },
};

// ---------------------------------------------------------------------------
// CSS — hover / active states kept out of inline styles for performance
// ---------------------------------------------------------------------------

const CSS = `
  /* Tab */
  .mr-tab:hover            { color:${C.text}; background:${C.bgHover}; }
  .mr-tab--active          { color:${C.blue}; font-weight:600;
                             border-bottom-color:${C.blue} !important;
                             background:${C.bg}; }
  .mr-tab--active:hover    { background:${C.bg}; }

  /* Large button */
  .mr-btn:hover            { background:${C.bgHover}; border-color:${C.border}; }
  .mr-btn:hover .ant-tooltip-open { background:${C.bgHover}; }
  .mr-btn--active          { background:${C.blueBg}; border-color:${C.blue}; }
  .mr-btn--active:hover    { background:${C.blueBg}; }
  .mr-btn--danger:hover    { background:${C.redBg}; border-color:${C.red}; }
  .mr-btn--danger:hover span { color:${C.red}; }
  .mr-btn--disabled        { opacity:.45; cursor:not-allowed !important; }

  /* Small button */
  .mr-sbtn:hover           { background:${C.bgHover}; border-color:${C.border}; }
  .mr-sbtn--active         { background:${C.blueBg}; border-color:${C.blue}; }
  .mr-sbtn--disabled       { opacity:.45; cursor:not-allowed !important; }

  /* Scrollbar in tab panel */
  .mr-panel::-webkit-scrollbar { height:4px; }
  .mr-panel::-webkit-scrollbar-thumb { background:${C.border}; border-radius:2px; }

  /* Ribbon search input */
  .ribbon-search .ant-input { font-size:12px !important; }
  .ribbon-search .ant-input::placeholder { color:${C.textMuted} !important; }
  .ribbon-search .ant-input-prefix { margin-right:5px; }
`;
